/**
 /**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import {CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule} from '@angular/core';
import localeTr from '@angular/common/locales/tr';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {NgxEchartsModule} from 'ngx-echarts';
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {ThemeModule} from './@theme/theme.module';
import {AppComponent} from './app.component';
import {AppRoutingModule} from './app-routing.module';
import {
  NB_TIME_PICKER_CONFIG,
  NbButtonModule,
  NbCalendarKitModule,
  NbCalendarModule,
  NbCalendarRangeModule,
  NbCardModule, NbCheckboxModule,
  NbDatepickerModule,
  NbDialogModule,
  NbFormFieldModule,
  NbIconModule,
  NbInputModule,
  NbLayoutModule,
  NbMenuModule,
  NbSelectModule,
  NbSidebarModule,
  NbTabsetModule,
  NbTimepickerModule,
  NbToastrModule,
  NbTooltipModule,
  NbWindowModule
} from '@nebular/theme';
import {UserService} from './core/services/data/user.service';
import {AuthService} from './core/services/auth/auth.service';
import {PageService} from './core/services/meta/page.service';
import {UserPreferenceService} from './core/services/meta/user-preference.service';
import {LayerService} from './core/services/meta/layer.service';
import {TagService} from './core/services/meta/tag.service';
import {RegionService} from './core/services/meta/region.service';
import {RealmService} from './core/services/meta/realm.service';
import {StructureDefinitionService} from './core/services/meta/structure-definition.service';
import {KPIService} from './core/services/meta/kpi.service';
import {PatternService} from './core/services/meta/pattern.service';
import {RequestTrackerService} from './core/services/http/request-tracker.service';
import {SocketService} from './core/services/http/socket.service';
import {LayerMarkerClickHandlerService} from './core/services/layer-marker-click-handler.service';
import {AuthGuard} from './core/guards/auth.guard';
import {NoAuthGuard} from './core/guards/no-auth.guard';
import {AdminGuard} from './core/guards/admin.guard';
import {RequestTrackerInterceptor} from './core/interceptors/request-tracker.interceptor';
import {PageResolver} from './core/resolvers/page.resolver';
import {AddPanelDialogComponent} from './modules/dialog/add-panel-dialog/add-panel-dialog.component';
import {CreateDialogComponent} from './modules/dialog/create-dialog/create-dialog.component';
import {ConfirmationDialogComponent} from './modules/dialog/confirmation-dialog/confirmation-dialog.component';
import {GenericConfirmationDialogComponent} from './modules/dialog/confirmation-dialog/generic-confirmation-dialog.component';
import {DateSelectionDialogComponent} from './modules/dialog/date-selection-dialog/date-selection-dialog.component';
import {EditDialogComponent} from './modules/dialog/edit-dialog/edit-dialog.component';
import {environment} from '../environments/environment';
import {SharedModule} from './shared/shared.module';
import {APP_BASE_HREF, registerLocaleData} from '@angular/common';
import {LocaleId} from './shared/translate/locale-id';
import {CreatePageDialogComponent} from './modules/dialog/create-page-dialog/create-page-dialog.component';
import {AddLayerDialogComponent} from './modules/dialog/add-layer-dialog/add-layer-dialog.component';
import {AddIconDialogComponent} from './modules/dialog/add-icon-dialog/add-icon-dialog.component';
import {AuthorizationTokenInterceptor} from './core/interceptors/authorization-token.interceptor';
import {getBaseLocation} from './core/initializer/base-href-initializer';
import {UserManagementService} from './core/services/meta/user-management.service';
import {PayloadEmptyCheckInterceptor} from './core/interceptors/payload-empty-check.interceptor';
import {AuthScopeGuard} from './core/guards/auth-scope.guard';
import {ContextSourceService} from './core/services/meta/context-source.service';
import {PageTemplateService} from './core/services/meta/page-template.service';

import {LottieModule} from 'ngx-lottie';
import player from 'lottie-web';
import {PreloaderService} from './core/services/preloader.service';
import {CancelOperationDialogComponent} from './modules/dialog/confirmation-dialog/cancel-operation-dialog.component';
import {ErrorCatchingInterceptor} from './core/interceptors/error-catching.interceptor';
import {TitleService} from './core/services/title.service';
import {ThemeJsonInterceptor} from './core/interceptors/theme-json.interceptor';

// Note we need a separate function as it's required
// by the AOT compiler.
export function playerFactory() {
  return player;
}

@NgModule({
  declarations: [
    AppComponent,
    AddPanelDialogComponent,
    AddLayerDialogComponent,
    AddIconDialogComponent,
    CreatePageDialogComponent,
    ConfirmationDialogComponent,
    GenericConfirmationDialogComponent,
    CancelOperationDialogComponent,
    CreateDialogComponent,
    DateSelectionDialogComponent,
    EditDialogComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    LottieModule.forRoot({player: playerFactory}),
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts')
    }),
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbDatepickerModule.forRoot(),
    NbDialogModule.forRoot(),
    NbWindowModule.forRoot(),
    NbToastrModule.forRoot(),
    NbCardModule,
    NbTabsetModule,
    NbCalendarModule,
    NbIconModule,
    NbCalendarKitModule,
    NbCalendarRangeModule,
    NbButtonModule,
    NbFormFieldModule,
    NbSelectModule,
    NbInputModule,
    ThemeModule.forRoot(),
    TranslateModule.forRoot({
      defaultLanguage: environment.defaultLanguage,
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    FormsModule,
    SharedModule,
    NbTooltipModule,
    ReactiveFormsModule,
    NbLayoutModule,
    NbCheckboxModule,
    NbTimepickerModule
  ],
  providers: [
    PreloaderService,
    UserService,
    RequestTrackerService,
    AuthService,
    PageService,
    PageTemplateService,
    UserPreferenceService,
    LayerService,
    TagService,
    UserManagementService,
    RegionService,
    RealmService,
    PatternService,
    StructureDefinitionService,
    KPIService,
    ContextSourceService,
    SocketService,
    AuthGuard,
    AuthScopeGuard,
    NoAuthGuard,
    AdminGuard,
    PageResolver,
    {provide: HTTP_INTERCEPTORS, useClass: RequestTrackerInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: ErrorCatchingInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: AuthorizationTokenInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: PayloadEmptyCheckInterceptor, multi: true},
    { provide: HTTP_INTERCEPTORS, useClass: ThemeJsonInterceptor, multi: true },
    {provide: APP_BASE_HREF, useFactory: getBaseLocation},
    // LOCALE_ID is set dynamically thanks to LocaleId class.
    // It is used by several components such as Nebular DatePicker to localize their outputs.
    {
      provide: LOCALE_ID,
      deps: [TranslateService],
      useClass: LocaleId
    },
    {provide: NB_TIME_PICKER_CONFIG, useValue: {}},
    TitleService
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
  // adding the service to the constructor so that it's instantiated. As the service is not referenced directly, it should somehow be instantiated
  // see (https://stackoverflow.com/questions/41215226/make-sure-a-service-is-instantiated)
  constructor(private layerMarkerClickHandlerService: LayerMarkerClickHandlerService) {
  }
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

// Register Turkish locale so that it can be used internally by Angular
registerLocaleData(localeTr);
